// @flow
import * as Immutable from 'immutable'; // This is required
import ImmutableModel from 'common/models/flow/ImmutableModel'; // Make sure you copied this file into your repo

import { BusinessUnitId, BusinessUnit } from 'common/models/flow/BusinessUnits';
import { AuditMetadata } from 'common/models/flow/AuditMetadata';

import type {
    BusinessUnitIdModelType,
    BusinessUnitModelType
} from 'common/models/flow/BusinessUnits';
import type { AuditMetadataModelType } from 'common/models/flow/AuditMetadata';
import type { DispatcherVisibilitySettingsModelType } from './Client';
import { CityId } from 'common/models/flow/CityId';
import type { CityIdModelType } from 'common/models/flow/CityId';

type TeamStatusEnum = 'ACTIVE' | 'INACTIVE';

export type MasterTeamModelType = {
    clientId: string,
    id: string,
    code: string,
    name: string,
    color: string,
    status: TeamStatusEnum,
    businessUnit: ?BusinessUnitModelType,
    businessUnitId: ?BusinessUnitIdModelType,
    teamPreferences: ?{
        dispatcherVisibilitySettings: DispatcherVisibilitySettingsModelType
    },
    auditMetadata: ?AuditMetadataModelType,
    customProperties: { [key: string]: string },
    version: number,
    cityId: ?CityIdModelType,
    shortCode: string
};

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class MasterTeam extends ImmutableModel {
    static fromJS(json: MasterTeamModelType): MasterTeam {
        const state: Object = Object.assign({}, json);
        state.businessUnit = state.businessUnit
            ? BusinessUnit.fromJS(state.businessUnit)
            : state.businessUnit;
        state.businessUnitId = state.businessUnitId
            ? BusinessUnitId.fromJS(state.businessUnitId)
            : state.businessUnitId;
        state.auditMetadata = state.auditMetadata
            ? AuditMetadata.fromJS(state.auditMetadata)
            : state.auditMetadata;
        state.customProperties = Immutable.Map(state.customProperties);
        state.cityId = state.cityId
            ? CityId.fromJS(state.cityId)
            : state.cityId;
        return new this(Immutable.Map(state));
    }

    toJS(): MasterTeamModelType {
        return {
            clientId: this.clientId,
            id: this.id,
            code: this.code,
            name: this.name,
            color: this.color,
            status: this.status,
            businessUnit: this.businessUnit
                ? this.businessUnit.toJS()
                : this.businessUnit,
            businessUnitId: this.businessUnitId
                ? this.businessUnitId.toJS()
                : this.businessUnitId,
            teamPreferences: this.teamPreferences,
            auditMetadata: this.auditMetadata
                ? this.auditMetadata.toJS()
                : this.auditMetadata,
            customProperties: this.customProperties.toObject(),
            version: this.version,
            cityId: this.cityId ? this.cityId.toJS() : this.cityId,
            shortCode: this.shortCode
        };
    }

    get clientId(): string {
        return this._state.get('clientId');
    }

    setClientId(clientId: string): this {
        return this.clone(this._state.set('clientId', clientId));
    }

    get id(): string {
        return this._state.get('id');
    }

    setId(id: string): this {
        return this.clone(this._state.set('id', id));
    }

    get code(): string {
        return this._state.get('code');
    }

    setCode(code: string): this {
        return this.clone(this._state.set('code', code));
    }

    get name(): string {
        return this._state.get('name');
    }

    setName(name: string): this {
        return this.clone(this._state.set('name', name));
    }

    get color(): string {
        return this._state.get('color');
    }

    setColor(color: string): this {
        return this.clone(this._state.set('color', color));
    }

    get status(): TeamStatusEnum {
        return this._state.get('status');
    }

    setStatus(status: TeamStatusEnum): this {
        return this.clone(this._state.set('status', status));
    }

    get businessUnit(): ?BusinessUnit {
        return this._state.get('businessUnit');
    }

    setBusinessUnit(businessUnit: ?BusinessUnit): this {
        return this.clone(this._state.set('businessUnit', businessUnit));
    }

    get businessUnitId(): ?BusinessUnitId {
        return this._state.get('businessUnitId');
    }

    setBusinessUnitId(businessUnitId: ?BusinessUnitId): this {
        return this.clone(this._state.set('businessUnitId', businessUnitId));
    }

    get teamPreferences(): ?{
        dispatcherVisibilitySettings: DispatcherVisibilitySettingsModelType
    } {
        return this._state.get('teamPreferences');
    }

    setTeamPreferences(
        teamPreferences: ?{
            dispatcherVisibilitySettings: DispatcherVisibilitySettingsModelType
        }
    ): this {
        return this.clone(this._state.set('teamPreferences', teamPreferences));
    }

    get auditMetadata(): ?AuditMetadata {
        return this._state.get('auditMetadata');
    }

    setAuditMetadata(auditMetadata: ?AuditMetadata): this {
        return this.clone(this._state.set('auditMetadata', auditMetadata));
    }

    get customProperties(): Immutable.Map<string, string> {
        return this._state.get('customProperties');
    }

    setCustomProperties(customProperties: Immutable.Map<string, string>): this {
        return this.clone(
            this._state.set('customProperties', customProperties)
        );
    }

    get version(): number {
        return this._state.get('version');
    }

    setVersion(version: number): this {
        return this.clone(this._state.set('version', version));
    }

    get cityId(): ?CityId {
        return this._state.get('cityId');
    }

    setCityId(cityId: ?CityId): this {
        return this.clone(this._state.set('cityId', cityId));
    }

    get shortCode(): string {
        return this._state.get('shortCode');
    }

    setShortCode(shortCode: string): this {
        return this.clone(this._state.set('shortCode', shortCode));
    }
}
